import { useId, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from './Badge';
import { Button } from './Button';
import { Input } from './Input';
import { addTags } from './fieldData';
export function TagInput({
  label,
  value,
  onChange,
  inputValue,
  onInputChange,
  maxTags = 8,
  maxLength = 40,
  disabled,
}: {
  label: string;
  value: readonly string[];
  onChange: (tags: string[]) => void;
  inputValue: string;
  onInputChange: (raw: string) => void;
  maxTags?: number;
  maxLength?: number;
  disabled?: boolean;
}) {
  const id = useId(),
    input = useRef<HTMLInputElement>(null),
    [error, setError] = useState<string | null>(null),
    [notice, setNotice] = useState('');
  const commit = (raw: string) => {
    const next = addTags([...value], raw, maxTags, maxLength);
    setError(next.error);
    if (next.error) {
      onInputChange(next.remainder);
      return;
    }
    onChange(next.tags);
    onInputChange(next.remainder);
    setNotice(next.tags.length > value.length ? 'Теги добавлены.' : 'Тег уже есть в списке.');
  };
  const remove = (index: number) => {
    const old = value[index];
    onChange(value.filter((_, i) => i !== index));
    setError(null);
    setNotice('Тег «' + old + '» удалён.');
    requestAnimationFrame(() => input.current?.focus());
  };
  return (
    <div data-slot="tag-input" className="min-w-0">
      <label htmlFor={id} className="mb-1.5 block text-xs font-medium text-fg-muted">
        {label}
      </label>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Input
            id={id}
            ref={input}
            value={inputValue}
            disabled={disabled}
            autoComplete="off"
            maxLength={512}
            placeholder="Название тега"
            helper={'До ' + maxTags + ' тегов · Enter или запятая'}
            error={error ?? undefined}
            errorTone="strong"
            onChange={(e) => {
              onInputChange(e.target.value);
              setError(null);
            }}
            onPaste={(e) => {
              const text = e.clipboardData.getData('text/plain');
              if (/[,;\n]/u.test(text)) {
                e.preventDefault();
                const start = e.currentTarget.selectionStart ?? inputValue.length;
                const end = e.currentTarget.selectionEnd ?? start;
                const candidate = inputValue.slice(0, start) + text + inputValue.slice(end);
                if (candidate.length > 512) {
                  setError('Вставка слишком длинная: не более 512 символов за раз.');
                  return;
                }
                commit(candidate);
              }
            }}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                if (inputValue.trim()) commit(inputValue);
              } else if (e.key === 'Backspace' && !inputValue && value.length) {
                e.preventDefault();
                document.getElementById(id + '-remove-' + (value.length - 1))?.focus();
              }
            }}
          />
        </div>
        <Button
          type="button"
          className="shrink-0"
          variant="secondary"
          disabled={disabled || !inputValue.trim()}
          aria-label="Добавить тег"
          onClick={() => commit(inputValue)}
        >
          <Plus className="size-4" />
          <span className="sr-only">Добавить</span>
        </Button>
      </div>
      {value.length > 0 && (
        <ul aria-label="Добавленные теги" className="mt-3 flex flex-wrap gap-2">
          {value.map((tag, i) => (
            <li key={tag}>
              <Badge>
                <span className="max-w-48 truncate py-1" title={tag}>
                  {tag}
                </span>
                <button
                  id={id + '-remove-' + i}
                  type="button"
                  disabled={disabled}
                  aria-label={'Удалить тег «' + tag + '»'}
                  onClick={() => remove(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' || e.key === 'Delete') {
                      e.preventDefault();
                      remove(i);
                    }
                  }}
                  className="rounded-full p-1 text-fg-muted hover:bg-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50"
                >
                  <X className="size-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      )}
      <p role="status" className="sr-only">
        {notice}
      </p>
    </div>
  );
}
